import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Avatar,
  Chip,
  CircularProgress,
  Divider
} from '@mui/material';
import {
  Engineering as MechanicIcon,
  DirectionsCar as DriverIcon,
  Business as ManagementIcon,
  Email as EmailIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { user as userApi } from '../../utils/api';
import { car as carApi } from '../../utils/api';
import { useAuth } from '../../components/auth/AuthModule';
import { getTransformedImageUrl } from '../../utils/filestack';
import PublicNavbar from '../../components/shared/PublicNavbar';
import api from '../../utils/axiosConfig';
import { motion } from 'framer-motion';

// Placeholder image path
const PLACEHOLDER_IMAGE = '/assets/images/placeholder-profile.jpg';

const TeamPage = () => {
  const { t } = useTranslation(['team', 'common']);
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [userCars, setUserCars] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageErrors, setImageErrors] = useState({});
  const [backgroundImage, setBackgroundImage] = useState(null);

  useEffect(() => {
    fetchUsersAndCars();
  }, [currentUser?.role]);

  // Fisher-Yates shuffle algorithm
  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  const fetchUsersAndCars = async () => {
    try {
      setLoading(true);
      const [userData, carsData, galleryRes] = await Promise.all([
        userApi.getPublic(),
        carApi.getPublic(),
        api.get('/galleries/random-image')
      ]);

      // Extract the users array from the response and ensure it's an array
      const users = userData?.users || [];
      
      // Ensure carsData is an array
      const cars = Array.isArray(carsData) ? carsData : [];
      
      // Create a map of user IDs to their first car
      const userCarsMap = cars.reduce((acc, car) => {
        if (car.user && !acc[car.user._id]) {
          acc[car.user._id] = car;
        }
        return acc;
      }, {});

      // Group users by role
      const grouped = users.reduce((acc, user) => {
        const role = user.role || 'driver';
        if (!acc[role]) acc[role] = [];
        acc[role].push(user);
        return acc;
      }, {});

      // Shuffle the drivers array
      if (grouped.driver) {
        grouped.driver = shuffleArray(grouped.driver);
      }

      setUsers(grouped);
      setUserCars(userCarsMap);
      
      // Fix for nested URL structure
      if (galleryRes.data && galleryRes.data.url) {
        if (typeof galleryRes.data.url === 'string') {
          setBackgroundImage(galleryRes.data.url);
        } else if (galleryRes.data.url.url) {
          setBackgroundImage(galleryRes.data.url.url);
        }
      }
    } catch (err) {
      // Don't log errors on public pages
      setError(t('errors.fetchingUsers'));
    } finally {
      setLoading(false);
    }
  };

  const handleImageError = (userId) => {
    setImageErrors(prev => ({
      ...prev,
      [userId]: true
    }));
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'mechanic':
        return <MechanicIcon fontSize="large" />;
      case 'management':
        return <ManagementIcon fontSize="large" />;
      case 'driver':
        return <DriverIcon fontSize="large" />;
      default:
        return <DriverIcon fontSize="large" />;
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'mechanic':
        return 'info';
      case 'management':
        return 'primary';
      case 'driver':
        return 'success';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  const getImageUrl = (member) => {
    if (imageErrors[member._id] || !member.imageUrl) {
      return PLACEHOLDER_IMAGE;
    }
    return getTransformedImageUrl(member.imageUrl, 'gallery');
  };
  
  const renderOperationsMember = (member, index) => (
    <Grid item xs={6} sm={6} md={12/7} key={member._id}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: index * 0.1 }}
        whileHover={{ 
          scale: 1.05,
          transition: { duration: 0.2 }
        }}
      >
        <Box sx={{ p: { xs: 2, sm: 1, md: 0 } }}>
          <CardMedia
            component="img"
            height="240"
            image={getImageUrl(member)}
            onError={() => handleImageError(member._id)}
            alt={member.name}
            sx={{ 
              borderRadius: 1,
              mb: 1,
              objectFit: 'cover',
              backgroundColor: 'grey.100'
            }}
          />
          <Typography variant="h6" gutterBottom>
            {member.name || member.username}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t(`roles.${member.role}`)}
          </Typography>
        </Box>
      </motion.div>
    </Grid>
  );

  const renderDriver = (member, index) => {
    const userCar = userCars[member._id];
    const carModel = userCar ? userCar.model : t('common:unknown');

    return (
      <Grid item xs={6} sm={6} md={12/7} key={member._id}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: index * 0.1 }}
          whileHover={{ 
            scale: 1.05,
            transition: { duration: 0.2 }
          }}
        >
          <Box sx={{ p: { xs: 2, sm: 1, md: 0 } }}>
            <CardMedia
              component="img"
              height="240"
              image={getImageUrl(member)}
              onError={() => handleImageError(member._id)}
              alt={member.name}
              sx={{ 
                borderRadius: 1,
                mb: 1,
                objectFit: 'cover',
                backgroundColor: 'grey.100'
              }}
            />
            <Typography mb={0} variant="h6" gutterBottom>
              {member.name || member.username}
            </Typography>
            <Typography mt={0} variant="body2" color="text.secondary">
              {carModel}
            </Typography>
          </Box>
        </motion.div>
      </Grid>
    );
  };

  return (
    <Box sx={{ position: 'relative', overflow: 'hidden', bgcolor: 'black', color: 'white', minHeight: '100vh' }}>
      {backgroundImage && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `url(${backgroundImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 0.25,
            zIndex: 0
          }}
        />
      )}
      <PublicNavbar />
      <Container maxWidth="xl" sx={{ py: 14, position: 'relative', zIndex: 1 }}>
        {/* Operations Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
        >
          <Box mb={8}>
            <Typography 
              variant="h3" 
              component="h1" 
              gutterBottom
              sx={{ 
                fontWeight: 'bold',
                position: 'relative',
                display: 'inline-block',
                color: 'white',
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                '&:after': {
                  content: '""',
                  position: 'absolute',
                  bottom: -8,
                  left: 0,
                  width: '100%',
                  height: 4,
                  backgroundColor: 'error.main'
                }
              }}
            >
              Operations
            </Typography>
            <Grid container spacing={4} sx={{ mt: 0 }}>
              {users.management && users.management.map((member, index) => renderOperationsMember(member, index))}
              {users.mechanic && users.mechanic.map((member, index) => renderOperationsMember(member, index + (users.management?.length || 0)))}
            </Grid>
          </Box>
        </motion.div>

        {/* Drivers Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.3 }}
        >
          <Box sx={{ position: 'relative' }}>
            <Typography 
              variant="h3" 
              component="h2" 
              gutterBottom
              sx={{ 
                fontWeight: 'bold',
                position: 'relative',
                display: 'inline-block',
                color: 'white',
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                '&:after': {
                  content: '""',
                  position: 'absolute',
                  bottom: -8,
                  left: 0,
                  width: '100%',
                  height: 4,
                  backgroundColor: 'error.main'
                }
              }}
            >
              Drivers
            </Typography>
            <Grid container spacing={4} sx={{ mt: 0 }}>
              {users.driver && users.driver.map((member, index) => renderDriver(member, index))}
            </Grid>
          </Box>
        </motion.div>
      </Container>
      
      {/* Diagonal Red Section */}
      <Box
        sx={{
          position: 'absolute',
          top: '45%',
          left: '-5%',
          right: '-5%',
          bottom: '-5%',
          backgroundColor: 'error.main',
          transform: 'skewY(8deg)',
          transformOrigin: 'top left',
          zIndex: 0
        }}
      />
    </Box>
  );
};

export default TeamPage; 